import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Loader2, LocateFixed, MapPin, RefreshCw } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  geoErrorFor,
  geolocationPermission,
  getCurrentPosition,
  isGeoError,
  type GeoError,
  type GeoPermission,
  type GeoPosition,
} from "@/hooks/useGeolocation";
import { formatTimeIn } from "@/lib/datetime";
import { employeeApi, type CheckInPreview } from "@/lib/employee-api";
import { checkInBody } from "@/lib/punch";
import {
  accuracyLabel,
  checkInVerdict,
  describeLocation,
  type LocationSummary,
  type TodayAction,
} from "@/lib/today-status";

const TONE: Record<LocationSummary["tone"], string> = {
  good: "border-success/30 text-success",
  warn: "border-warning/40 text-warning",
  info: "border-info/30 text-info",
  neutral: "border-border text-muted-foreground",
};

interface Reading {
  position: GeoPosition;
  /** The server's own verdict (BR-153); null when it could not be asked. */
  preview: CheckInPreview | null;
  at: Date;
}

/**
 * Where the employee is relative to their work location — the web
 * counterpart of the app's location card (FEAT-2026-024).
 *
 * BR-158: location is read on its own only when the browser already allows it
 * for this site; otherwise nothing is asked until the employee presses the
 * button, so the page never raises a permission prompt on every visit. The
 * reading is advisory and never recorded. A punch still takes its own fresh
 * reading — reusing this one is how a position hours old ended up on a
 * check-out in the app (mobile BUG-2026-011).
 */
export function LocationCheckCard({
  tz,
  maxAccuracyM,
  action,
}: {
  tz: string;
  maxAccuracyM: number;
  action: TodayAction;
}) {
  const [permission, setPermission] = useState<GeoPermission | null>(null);
  const [reading, setReading] = useState<Reading | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<GeoError | null>(null);

  const check = useCallback(async () => {
    setChecking(true);
    setError(null);
    try {
      const position = await getCurrentPosition();
      setPermission("granted");
      let preview: CheckInPreview | null = null;
      try {
        preview = await employeeApi.previewCheckIn(checkInBody(position, navigator.userAgent));
      } catch {
        // Advisory: without the server's verdict the accuracy is still worth
        // showing, and nothing here may stand between the employee and a punch.
      }
      setReading({ position, preview, at: new Date() });
    } catch (e) {
      const err = isGeoError(e) ? e : geoErrorFor("unavailable");
      if (err.kind === "permission-denied") setPermission("denied");
      setError(err);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void geolocationPermission().then((state) => {
      if (cancelled) return;
      setPermission(state);
      if (state === "granted") void check();
    });
    return () => {
      cancelled = true;
    };
  }, [check]);

  const summary = reading?.preview ? describeLocation(reading.preview) : null;
  const verdict = reading?.preview ? checkInVerdict(reading.preview, action) : null;
  const coarse =
    reading != null &&
    (!Number.isFinite(reading.position.accuracy) || reading.position.accuracy > maxAccuracyM);

  let body: ReactNode;
  if (checking && !reading) {
    body = (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Checking your location…
      </p>
    );
  } else if (permission === "denied") {
    body = (
      <p className="text-sm text-muted-foreground">
        Location is blocked for this site in your browser. Allow it in your browser settings to
        check in from the web.
      </p>
    );
  } else if (error && !reading) {
    body = (
      <div className="space-y-2">
        <p className="text-sm text-destructive">{error.message}</p>
        <Button size="sm" variant="outline" onClick={() => void check()}>
          Try again
        </Button>
      </div>
    );
  } else if (reading) {
    body = (
      <div className="space-y-3">
        {summary ? (
          <p className="text-sm">{summary.detail}</p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Couldn't compare it with your work location just now.
          </p>
        )}
        <div className="grid grid-cols-2 gap-2 rounded-lg border p-3 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">GPS accuracy</p>
            <p className={coarse ? "font-medium text-warning" : "font-medium"}>
              <span>{accuracyLabel(reading.position.accuracy)}</span>
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Checked</p>
            <p className="font-medium">{formatTimeIn(reading.at, tz)}</p>
          </div>
        </div>
        {verdict && (
          <p className={verdict.allowed ? "text-sm text-success" : "text-sm text-warning"}>
            {verdict.message}
          </p>
        )}
        {error && <p className="text-sm text-destructive">{error.message}</p>}
        <Button size="sm" variant="outline" disabled={checking} onClick={() => void check()}>
          {checking ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Check again
        </Button>
      </div>
    );
  } else if (permission === null) {
    body = null;
  } else {
    body = (
      <div className="space-y-2">
        <Button variant="outline" onClick={() => void check()}>
          <LocateFixed className="mr-2 h-4 w-4" />
          Check my location
        </Button>
        <p className="text-xs text-muted-foreground">
          Your browser will ask to share your location. Nothing is recorded until you check in.
        </p>
      </div>
    );
  }

  return (
    <Card data-testid="today-location">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          Location
        </CardTitle>
        {summary && (
          <Badge variant="outline" className={TONE[summary.tone]}>
            {summary.label}
          </Badge>
        )}
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
